import { useEffect, useState } from "react";
import { UserPlus, ShieldCheck, ShieldOff, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { F, btn } from "@/components/CaseBits";

const CLS = { approved: "bg-green-50 text-green-900", pending: "bg-amber-50 text-amber-900", deactivated: "bg-red-50 text-red-900" };

export default function AdminArbitrators() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ name: "", email: "", phone: "", chamber: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/admin/arbitrators").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };
  const add = (e) => {
    e.preventDefault();
    act(async () => { await api.post("/admin/arbitrators", form); setForm({ name: "", email: "", phone: "", chamber: "" }); },
      "Arbitrator added to the approved panel — set-password e-mail sent");
  };
  const setApproval = (a, approval) => act(() => api.patch(`/admin/arbitrators/${a.id}`, { approval }), `${a.name}: ${approval}`);
  const remove = (a) => { if (window.confirm(`Remove ${a.name} from the panel?`)) act(() => api.delete(`/admin/arbitrators/${a.id}`), "Removed"); };

  const list = (rows || []).filter((r) => filter === "all" || r.approval === filter);
  const pending = (rows || []).filter((r) => r.approval === "pending").length;

  return (
    <div data-testid="admin-arbitrators-page">
      <PageHeader eyebrow="Admin" title="Panel of Arbitrators"
        description="Maintain the pre-approved list advocates choose from when filing a reference. Self-registered arbitrators wait here as pending until you approve them."
        testId="admin-arbitrators-header" />

      <form onSubmit={add} className="border border-border bg-card p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end" data-testid="admin-add-form">
        <F label="Name *" required value={form.name} onChange={(v) => set("name", v)} testId="admin-add-name" />
        <F label="Email *" type="email" required value={form.email} onChange={(v) => set("email", v)} testId="admin-add-email" />
        <F label="Phone (WhatsApp)" value={form.phone} onChange={(v) => set("phone", v)} testId="admin-add-phone" placeholder="+91…" />
        <F label="City / chamber" value={form.chamber} onChange={(v) => set("chamber", v)} testId="admin-add-chamber" />
        <Button type="submit" className={btn} data-testid="admin-add-submit"><UserPlus className="h-4 w-4 mr-2" /> Add to panel</Button>
      </form>

      <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="admin-filter">
        {["all", "approved", "pending", "deactivated"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors duration-200 ${filter === f ? "bg-blue-900 text-white border-blue-900" : "border-border hover:bg-accent"}`}>
            {f}{f === "pending" && pending > 0 ? ` (${pending})` : ""}
          </button>
        ))}
      </div>

      <div className="mt-4 border border-border bg-card overflow-x-auto">
        {!rows ? <div className="h-40 animate-pulse bg-muted" /> : list.length === 0 ? (
          <p className="p-10 text-sm text-muted-foreground" data-testid="admin-empty">No arbitrators in this view.</p>
        ) : (
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="border-b border-border bg-muted/60">
              {["Arbitrator", "Contact", "Status", "Cases", "Since", "Actions"].map((h) => <th key={h} className="p-4 text-left label-caps font-normal">{h}</th>)}
            </tr></thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-0" data-testid={`admin-arb-row-${a.email}`}>
                  <td className="p-4"><p className="font-medium">{a.name}</p><p className="text-xs text-muted-foreground">{a.chamber || "—"}{a.added_by_admin ? " · added by admin" : " · self-registered"}</p></td>
                  <td className="p-4 text-xs font-mono">{a.email}<br />{a.phone || ""}</td>
                  <td className="p-4"><Badge className={CLS[a.approval]} data-testid={`admin-arb-status-${a.email}`}>{a.approval}</Badge></td>
                  <td className="p-4 font-mono">{a.cases}</td>
                  <td className="p-4 text-muted-foreground whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-3 text-xs uppercase tracking-widest">
                      {a.approval !== "approved" && <button onClick={() => setApproval(a, "approved")} data-testid={`admin-approve-${a.email}`} className="inline-flex items-center gap-1 text-green-800 hover:underline"><ShieldCheck className="h-3.5 w-3.5" /> Approve</button>}
                      {a.approval === "approved" && <button onClick={() => setApproval(a, "pending")} data-testid={`admin-pend-${a.email}`} className="inline-flex items-center gap-1 text-amber-800 hover:underline"><Clock className="h-3.5 w-3.5" /> Set pending</button>}
                      {a.approval !== "deactivated" && <button onClick={() => setApproval(a, "deactivated")} data-testid={`admin-deactivate-${a.email}`} className="inline-flex items-center gap-1 text-red-800 hover:underline"><ShieldOff className="h-3.5 w-3.5" /> Deactivate</button>}
                      {a.cases === 0 && <button onClick={() => remove(a)} data-testid={`admin-remove-${a.email}`} className="inline-flex items-center gap-1 text-muted-foreground hover:text-red-800"><Trash2 className="h-3.5 w-3.5" /> Remove</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
